import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { H3, ListingLink } from '../../../../components';
import { LISTING_STATE_DRAFT } from '../../../../util/types';
import { buildListingTaxonomy } from '../../../../util/listingTaxonomy';
import EditListingCategoryForm from './EditListingCategoryForm';

import css from './EditListingCategoryPanel.module.css';

const getInitialValues = (params, selectedCategory) => {
  const { listing } = params;
  const { title } = listing?.attributes || {};
  const { category } = listing?.attributes.publicData || {};

  return { pub_category: category || selectedCategory, title };
};

/**
 * Listing type info to store on the draft.
 *
 * The marketplace has a single listing type ('free-listing'), so this is fixed —
 * but Sharetribe needs `listingType`, `transactionProcessAlias` and `unitType` on
 * the listing for the later steps and for `tabCompleted(DETAILS)` to work.
 */
const getTransactionInfo = listingTypes => {
  if (listingTypes.length !== 1) {
    return {};
  }
  const { listingType, transactionType } = listingTypes[0];
  return {
    listingType,
    transactionProcessAlias: transactionType.alias,
    unitType: transactionType.unitType,
  };
};

/**
 * First step of placing an advertisement.
 *
 * Submitting this creates the draft listing, which is the point: it gives us a
 * listing id, so the payment on the next step is attached to a real advertisement
 * from the moment Stripe creates the subscription. Previously the seller paid
 * before anything existed, and the subscription belonged to nothing until it was
 * linked up later.
 *
 * As little is asked for as possible — just the category. Sharetribe requires a
 * title on a draft, so the category's own name stands in until the details step
 * builds the real title from manufacturer, model and year.
 */
const EditListingCategoryPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    draftingInProgress,
    errors,
    config,
    selectedCategory,
  } = props;
  const intl = useIntl();

  const classes = classNames(rootClassName || css.root, className);

  const initialValues = getInitialValues(props, selectedCategory);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;
  const listingFields = config.listing.listingFields;
  const listingTypes = config.listing.listingTypes.filter(t => t.listingType === 'free-listing');

  const handleSubmit = values => {
    const { pub_category: category } = values;

    // Stand-in title. The details step overwrites it with manufacturer + model +
    // year; until then this is what the seller sees on their draft card.
    const categoryLabel = intl.formatMessage({ id: `General.listing.category.${category}` });

    onSubmit({
      title: categoryLabel,
      publicData: {
        category,
        // Kept in step with category on a draft that already has a type. The
        // details step writes it again with the final category and type.
        taxonomy: buildListingTaxonomy(category, listing?.attributes?.publicData?.type),
        ...getTransactionInfo(listingTypes),
      },
    });
  };

  return (
    <div className={classes}>
      <H3 as="h1">
        {isPublished ? (
          <FormattedMessage
            id="EditListingCategoryPanel.title"
            values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
          />
        ) : (
          <FormattedMessage
            id="EditListingCategoryPanel.createListingTitle"
            values={{ lineBreak: <br /> }}
          />
        )}
      </H3>
      <EditListingCategoryForm
        className={css.form}
        submitButtonText={submitButtonText}
        initialValues={initialValues}
        listingFieldsConfig={listingFields}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress || draftingInProgress}
        fetchErrors={errors}
        intl={intl}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default EditListingCategoryPanel;
